import { useMemo } from 'react'

import type { AnalyticsDataDecorationOverrides, AnalyticsReportDefinition } from '../../../types'
import { useAnalyticsReportFiltersQuery } from '../queries/useAnalyticsReportFiltersQuery'
import { useDynamicFiltersParams } from './useDynamicFiltersParams'
import { getDecorationOverridesFromFilterGroup } from './utils/getDecorationOverridesFromFilterGroup'

type UseAnalyticsReportFiltersParams = {
  reportDefinition: AnalyticsReportDefinition | undefined
}

export const useAnalyticsReportFilters = ({
  reportDefinition,
}: UseAnalyticsReportFiltersParams) => {
  const hasDynamicFilters = !!reportDefinition?.filterGroups?.some(
    (group) => group.filterType === 'dynamic',
  )

  const { analyticsReportFiltersQuery } = useAnalyticsReportFiltersQuery(
    [{ id: reportDefinition?.id || '' }],
    {
      enabled: hasDynamicFilters,
    },
  )

  const shopSpecificFilters = analyticsReportFiltersQuery?.data?.filterGroups

  const {
    currentFilters,
    filtersForAPI,
    filtersForVisibility,
    handleFilterChange,
    hasActiveFilters,
  } = useDynamicFiltersParams({
    reportDefinition,
    shopSpecificFilters,
  })

  const decorationOverrides = useMemo(() => {
    if (!reportDefinition?.filterGroups) return []

    const overrides: AnalyticsDataDecorationOverrides = []

    reportDefinition.filterGroups.forEach((group) => {
      if (group.filterType === 'static') {
        const groupOverrides = getDecorationOverridesFromFilterGroup(group, currentFilters)
        if (groupOverrides) {
          overrides.push(...groupOverrides)
        }
      }
    })

    if (shopSpecificFilters) {
      shopSpecificFilters.forEach((group) => {
        const groupOverrides = getDecorationOverridesFromFilterGroup(group, currentFilters)
        if (groupOverrides) {
          overrides.push(...groupOverrides)
        }
      })
    }

    return overrides
  }, [reportDefinition?.filterGroups, shopSpecificFilters, currentFilters])

  return {
    filters: currentFilters,
    filtersForAPI,
    filtersForVisibility,
    decorationOverrides,
    onChange: handleFilterChange,
    hasActiveFilters,
  }
}
